import { useQuery } from "@tanstack/react-query";
import type { DashboardMode } from "@nexus/contracts";
import { useIamPermission, usePermissions } from "@/features/auth/hooks/usePermissions";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { writeActivePresetId } from "@/features/dashboard/lib/active-preset-storage";
import { buildCurrentDrilldownPresetPayload } from "@/features/drilldown/lib/filter-preset-utils";
import {
  drilldownFilterPresetsQueryOptions,
  useCreateDrilldownFilterPreset,
} from "@/lib/queries/filter-presets.queries";
import { SaveFilterPresetControl } from "./SaveFilterPresetControl";

interface DrilldownSaveFilterPresetProps {
  mode: DashboardMode;
  canSavePreset?: boolean;
  onPresetCreated?: (presetId: string) => void;
}

export function DrilldownSaveFilterPreset({
  mode,
  canSavePreset = true,
  onPresetCreated,
}: DrilldownSaveFilterPresetProps) {
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const permissions = usePermissions();
  const canListPresets = useIamPermission(permissions.DRILLDOWN_VIEW_PAGE);
  const canShareWithCompany = useIamPermission(permissions.DRILLDOWN_SHARE_FILTER_PRESET);
  const createMutation = useCreateDrilldownFilterPreset(mode);
  const { data: presets = [] } = useQuery({
    ...drilldownFilterPresetsQueryOptions(mode, activeTenantId),
    enabled: canListPresets && Boolean(activeTenantId),
  });

  if (!canListPresets) return null;

  return (
    <SaveFilterPresetControl
      disabled={!canSavePreset}
      presets={presets}
      isPending={createMutation.isPending}
      i18nPrefix="drilldown.filterPresets"
      canShareWithCompany={canShareWithCompany}
      onSave={async (name, shareWithCompany) => {
        const payload = buildCurrentDrilldownPresetPayload(mode);
        const created = await createMutation.mutateAsync({
          name,
          shareWithCompany,
          ...payload,
        });
        writeActivePresetId("drilldown", mode, created.id);
        onPresetCreated?.(created.id);
      }}
    />
  );
}
